import React, { useEffect, useState, useRef } from "react";
import Layout from "../../components/layouts/Layout";
import BreadcrumbBlock from "../../components/BreadcrumbBlock/BreadcrumbBlock";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Table,
  Button,
  Modal,
  notification,
  Empty,
  Tooltip,
  Flex,
  Spin,
} from "antd";
import {
  ArrowLeftOutlined,
  PaperClipOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Home2 } from "iconsax-react";
import { mixGetAi, mixPostAi } from "../../services/apiAi";
import { mixGetV1 } from "../../services/mixin";
import { CSKH_API_KEY, CSKH_ENGINE_URL } from "../../env";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "../SupportInbox/SupportInbox.scss";
import FormattedMarkdown from "../../components/FormattedMarkdown/FormattedMarkdown";

dayjs.extend(utc);
dayjs.extend(timezone);

interface Conversation {
  id: string;
  ticket_no: string;
  tenant_id: string | null;
  seller_id: string | null;
  customer_id: string;
  customer_name: string;
  customer_email: string;
  channel: string;
  status: string;
  last_intent: string;
  last_message_at: string;
  created_at: string;
}

interface Message {
  id?: string;
  sender_type: "customer" | "bot" | "human_agent";
  content: string;
  created_at: string;
  image_urls?: string[];
  agent_name?: string;
}

interface InfoPlanStore {
  address: string;
  date_ex: string;
  date_expired: string;
  duration: number;
  email: string;
  income: number;
  is_expired: number;
  owner_name: string;
  phone_number: string;
  plan_name: string;
  status: number;
  status_name: string;
  store_name: string;
  create_at: string;
}

const formatMessageContent = (content: string) => {
  if (!content) return "";
  return <FormattedMarkdown content={content} />;
};

const TicketDetail: React.FC = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const navigate = useNavigate();
  const { id: activeConvId } = useParams<{ id: string }>();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [infoPackageStore, setInfoPackageStore] = useState<InfoPlanStore>();
  const [paymentHistoryVisible, setPaymentHistoryVisible] = useState(false);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [showAllMedia, setShowAllMedia] = useState(false);

  // Lightbox Modal for images
  const [lightboxVisible, setLightboxVisible] = useState(false);
  const [lightboxImg, setLightboxImg] = useState("");

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fetchingMessagesRef = useRef<string | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const shouldScrollToBottomRef = useRef(true);
  const messagesRef = useRef<Message[]>([]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    shouldScrollToBottomRef.current = true;
  }, [activeConvId]);

  // Derive active conversation object
  const activeConversation = conversations.find((c) => c.id === activeConvId);
  const activeStatus = activeConversation?.status;

  // Setup Notification helper
  const [api, contextHolder] = notification.useNotification();
  const openNotification = (
    type: "success" | "error" = "success",
    message = "Request success!",
  ) => {
    api[type]({
      message: message,
      placement: "bottomRight",
      className: "single-message",
    });
  };

  // 2. Fetch conversations list from AI Engine
  const fetchConversations = async () => {
    if (!user || !user?.id || !activeConvId) return;
    try {
      setLoading(true);
      // We are admin/seller side, so try to fetch admin list first
      let rawConvs: any[] = [];
      const resAdmin = await mixGetAi("/admin/conversations", {});
      if (resAdmin && Array.isArray(resAdmin)) {
        rawConvs = resAdmin.map((c: any) => ({
          ...c,
          id: c.conversation_id,
        }));
      }

      // If not found in admin list, try seller conversations list
      if (
        rawConvs.length === 0 ||
        !rawConvs.some((c) => c.id === activeConvId)
      ) {
        const resSeller = await mixGetAi("/conversations/list", {
          seller_id: String(user?.id),
        });
        rawConvs = Array.isArray(resSeller) ? resSeller : resSeller?.data || [];
      }

      setConversations(rawConvs);

      const targetConv = rawConvs.find((c: any) => c.id === activeConvId);
      if (targetConv) {
        fetchMessagesList(targetConv.id);
        gInpoPackgeStore(targetConv.customer_id);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessagesList = async (ticketId: string, showLoader = true) => {
    if (fetchingMessagesRef.current === ticketId) return;
    fetchingMessagesRef.current = ticketId;

    if (showLoader) setMsgLoading(true);
    try {
      const res = await mixGetAi(`/conversations/${ticketId}/messages`, {});
      const rawMsgs = Array.isArray(res) ? res : res?.data || [];

      // Sort messages ascending by created time
      rawMsgs.sort((a: any, b: any) => {
        return dayjs(a.created_at).unix() - dayjs(b.created_at).unix();
      });

      let scrollNeeded = false;
      if (shouldScrollToBottomRef.current) {
        scrollNeeded = true;
        shouldScrollToBottomRef.current = false;
      } else {
        const currentMessages = messagesRef.current;
        const currentLength = currentMessages.length;
        const newLength = rawMsgs.length;

        if (newLength > currentLength) {
          const lastNewMsg = rawMsgs[newLength - 1];
          const lastCurrentMsg = currentMessages[currentLength - 1];

          if (
            !lastCurrentMsg ||
            lastNewMsg.id !== lastCurrentMsg.id ||
            lastNewMsg.created_at !== lastCurrentMsg.created_at
          ) {
            const container = chatContainerRef.current;
            const isUserAtBottom = container
              ? container.scrollHeight -
                  container.scrollTop -
                  container.clientHeight <=
                150
              : true;

            if (lastNewMsg.sender_type === "human_agent" || isUserAtBottom) {
              scrollNeeded = true;
            }
          }
        }
      }

      setMessages(rawMsgs);

      if (scrollNeeded) {
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop =
              chatContainerRef.current.scrollHeight;
          }
        }, 100);
      }
    } catch (error) {
      console.error("Error loading conversation messages:", error);
    } finally {
      if (showLoader) setMsgLoading(false);
      fetchingMessagesRef.current = null;
    }
  };

  // get Inpo Packge store
  const gInpoPackgeStore = async (storeId: string) => {
    try {
      const res = await mixGetV1(`/api/webAdmin/gInfoPackgeStore.php`, {
        store_id: storeId,
      });
      setInfoPackageStore(res?.data ?? {});
    } catch (error) {
      console.error("Error loading conversation messages:", error);
    }
  };

  const fetchPaymentHistory = async (storeId: string) => {
    try {
      setPaymentHistoryLoading(true);
      setPaymentHistoryVisible(true);
      const res = await mixGetV1(`/api/webAdmin/gHistoryPaymentStore.php`, {
        store_id: storeId,
      });
      setPaymentHistory(res?.data ?? []);
    } catch (error) {
      console.error("Error fetching payment history:", error);
    } finally {
      setPaymentHistoryLoading(false);
    }
  };

  // Polling for detail view messages
  useEffect(() => {
    let intervalId: any = null;
    if (activeConvId && activeStatus && activeStatus !== "resolved") {
      intervalId = setInterval(() => {
        fetchMessagesList(activeConvId, false);
      }, 4000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [activeConvId, activeStatus]);

  // Initial loading
  useEffect(() => {
    if (!user || (user?.role !== 3 && user?.role !== 1)) {
      navigate("/");
      return;
    }
    fetchConversations();
  }, [activeConvId]);

  // Reset showAllMedia state when active conversation changes
  useEffect(() => {
    setShowAllMedia(false);
  }, [activeConvId]);

  // Handle send message from agent (CSKH)
  const handleSendAgentReply = async () => {
    if (!replyText.trim() || !activeConvId) return;
    const msgText = replyText;
    setReplyText("");
    setReplying(true);

    // Optimistically add to UI messages list
    const tempMsg: Message = {
      sender_type: "human_agent",
      content: msgText,
      created_at: dayjs().toISOString(),
      agent_name: user?.owner_name || "Support Agent",
    };
    shouldScrollToBottomRef.current = true;
    setMessages((prev) => [...prev, tempMsg]);
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    }, 50);

    try {
      await mixPostAi(`/conversations/${activeConvId}/agent-reply`, {
        message: msgText,
        agent_name: user?.owner_name || "Support Agent",
      });
      openNotification("success", "Reply sent successfully");
      shouldScrollToBottomRef.current = true;
      await fetchMessagesList(activeConvId, false);
      fetchConversations();
    } catch (error) {
      console.error("Error replying to conversation:", error);
    } finally {
      setReplying(false);
    }
  };

  // Handle resolving the conversation
  const handleResolveTicket = async () => {
    if (!activeConvId) return;
    setResolving(true);
    try {
      await mixPostAi(`/conversations/${activeConvId}/status`, {
        status: "resolved",
      });
      openNotification("success", "Ticket marked as Resolved");

      // Update local state dynamically
      setConversations((prev) =>
        prev.map((c) =>
          c.id === activeConvId ? { ...c, status: "resolved" } : c,
        ),
      );

      shouldScrollToBottomRef.current = true;
      await fetchMessagesList(activeConvId, false);
    } catch (error) {
      console.error("Error resolving conversation:", error);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === activeConvId ? { ...c, status: "resolved" } : c,
        ),
      );
    } finally {
      setResolving(false);
    }
  };

  // Handle switching conversation status to "waiting_for_human" (Handle button)
  const handleTakeOverTicket = async () => {
    if (!activeConvId || activeConversation?.status === "waiting_for_human")
      return;
    setResolving(true);
    try {
      await mixPostAi(`/conversations/${activeConvId}/status`, {
        status: "waiting_for_human",
      });
      openNotification(
        "success",
        "Switched status to Need Human. You can now reply.",
      );

      // Update local state dynamically
      setConversations((prev) =>
        prev.map((c) =>
          c.id === activeConvId ? { ...c, status: "waiting_for_human" } : c,
        ),
      );

      shouldScrollToBottomRef.current = true;
      await fetchMessagesList(activeConvId, false);
    } catch (error) {
      console.error("Error switching status to human handle:", error);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === activeConvId ? { ...c, status: "waiting_for_human" } : c,
        ),
      );
    } finally {
      setResolving(false);
    }
  };

  // Handle reopening the conversation (chat lại)
  const handleReopenTicket = async () => {
    if (!activeConvId) return;
    setResolving(true);
    try {
      await mixPostAi(`/conversations/${activeConvId}/status`, {
        status: "waiting_for_human",
      });
      openNotification("success", "Ticket reopened successfully");

      // Update local state dynamically
      setConversations((prev) =>
        prev.map((c) =>
          c.id === activeConvId ? { ...c, status: "waiting_for_human" } : c,
        ),
      );

      shouldScrollToBottomRef.current = true;
      await fetchMessagesList(activeConvId, false);
    } catch (error) {
      console.error("Error reopening conversation:", error);
    } finally {
      setResolving(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !activeConvId) return;

    // Validate size for all files
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > 5 * 1024 * 1024) {
        alert(`File ${files[i].name} exceeds the 5MB size limit.`);
        return;
      }
    }

    setUploadingFile(true);

    try {
      const myHeaders = new Headers();
      myHeaders.append("x-api-key", CSKH_API_KEY);

      // Upload files in parallel
      const uploadPromises = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(
          `${CSKH_ENGINE_URL}/api/v1/conversations/${activeConvId}/media?sender_type=human_agent`,
          {
            method: "POST",
            headers: myHeaders,
            body: formData,
          },
        );

        if (!response.ok) {
          throw new Error(
            `Upload failed for ${file.name} with status: ${response.status}`,
          );
        }
      });

      await Promise.all(uploadPromises);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      shouldScrollToBottomRef.current = true;
      await fetchMessagesList(activeConvId, false);
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Failed to send some or all files");
    } finally {
      setUploadingFile(false);
    }
  };

  // Helper: Format Chicago Time Zone
  const formatChicago = (
    dateStr: string,
    formatPattern = "YYYY-MM-DD HH:mm",
  ) => {
    if (!dateStr) return "";
    return dayjs(dateStr).tz("America/Chicago").format(formatPattern);
  };

  // Helper: Check if URL is a video
  const isVideoUrl = (url: string) => {
    if (!url) return false;
    const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".m4v"];
    const urlLower = url.toLowerCase();
    return (
      videoExtensions.some(
        (ext) => urlLower.endsWith(ext) || urlLower.includes(ext + "?"),
      ) || urlLower.startsWith("data:video/")
    );
  };

  // Extract all media/images uploaded in conversation for the right sidebar
  const mediaList = messages.flatMap((m) => m.image_urls || []);

  const mediaItems: { url: string; created_at: string }[] = [];
  messages.forEach((m) => {
    if (m.image_urls) {
      m.image_urls.forEach((url) => {
        mediaItems.push({ url, created_at: m.created_at });
      });
    }
  });

  const dateGroups = Array.from(
    new Set(
      mediaItems.map((item) => formatChicago(item.created_at, "MMM DD, YYYY")),
    ),
  );
  dateGroups.reverse();

  if (loading && conversations.length === 0) {
    return (
      <Layout contentClassName="support-inbox-page">
        <Flex justify="center" align="center" style={{ minHeight: "80vh" }}>
          <Spin size="large" tip="Loading conversation details..." />
        </Flex>
      </Layout>
    );
  }

  return (
    <>
      {contextHolder}
      <Layout contentClassName="support-inbox-page">
        <BreadcrumbBlock
          items={[
            {
              title: (
                <Flex align="center" gap={16}>
                  <Home2 variant="Bulk" />
                  <Link to="/">CMD Tech</Link>
                </Flex>
              ),
            },
            { title: "Store Management" },
            { title: "Conversation Audit", link: "/ticket-list" },
            { title: "Conversation Detail" },
          ]}
          title="Conversation Detail"
          description=""
        />

        <div className="inbox-detail-view-container">
          {/* Top Detail Header */}
          <div className="detail-view-header">
            <div className="title-section">
              <span className="subtitle-hash">
                #TK-{activeConversation?.ticket_no}
              </span>
            </div>
            <Button
              icon={<ArrowLeftOutlined />}
              className="back-btn-classic"
              onClick={() => {
                navigate(-1);
              }}
            >
              Back
            </Button>
          </div>

          {/* Grid Layout (Left: Chat & Input, Right: Store Context Panels) */}
          <div className="detail-grid">
            {/* Left Chat Area */}
            <div className="chat-column">
              <div className="chat-card-header">
                <h3 className="store-owner-title">
                  {activeConversation?.customer_name}
                  {activeConversation?.customer_email
                    ? ` - [${activeConversation?.customer_email}]`
                    : ""}
                </h3>
              </div>

              {/* Messages container */}
              <div className="chat-messages-container" ref={chatContainerRef}>
                {msgLoading ? (
                  <div className="chat-panel-loader">
                    <div className="spinner"></div>
                    <span>Loading messages history...</span>
                  </div>
                ) : (
                  <div className="chat-bubbles-list">
                    {messages.map((msg, index) => {
                      const isAgent = msg.sender_type === "human_agent";
                      const isBot = msg.sender_type === "bot";
                      const senderName = isAgent
                        ? msg.agent_name || "Support Agent"
                        : isBot
                          ? "Bot"
                          : "Guest";

                      return (
                        <div
                          key={index}
                          className={`chat-bubble-row ${isAgent ? "agent-bubble-row" : "external-bubble-row"}`}
                        >
                          <div className="sender-avatar">
                            {isAgent ? (
                              <UserOutlined style={{ color: "#fff" }} />
                            ) : isBot ? (
                              <InfoCircleOutlined
                                style={{ color: "#faad14" }}
                              />
                            ) : (
                              <UserOutlined style={{ color: "#808191" }} />
                            )}
                          </div>
                          <div className="content-bubble-wrapper">
                            <span className="sender-meta-name">
                              {senderName}{" "}
                              <span className="meta-time">
                                {formatChicago(msg.created_at, "HH:mm")}
                              </span>
                            </span>
                            <div className="bubble-text">
                              {formatMessageContent(msg.content)}
                            </div>
                            {msg.image_urls && msg.image_urls.length > 0 && (
                              <div className="bubble-images-grid">
                                {msg.image_urls.map((img, idx) => {
                                  if (isVideoUrl(img)) {
                                    return (
                                      <video
                                        key={idx}
                                        src={img}
                                        className="chat-attachment-thumbnail"
                                        style={{
                                          maxWidth: "200px",
                                          maxHeight: "150px",
                                          borderRadius: "8px",
                                          objectFit: "cover",
                                          cursor: "pointer",
                                        }}
                                        onClick={() => {
                                          setLightboxImg(img);
                                          setLightboxVisible(true);
                                        }}
                                      />
                                    );
                                  }
                                  return (
                                    <img
                                      key={idx}
                                      src={img}
                                      alt="Uploaded by customer"
                                      className="chat-attachment-thumbnail"
                                      onClick={() => {
                                        setLightboxImg(img);
                                        setLightboxVisible(true);
                                      }}
                                    />
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Input action panels */}
              {(() => {
                const currentUser = JSON.parse(
                  localStorage.getItem("user") || "{}",
                );
                const currentRoleId = Number(currentUser?.role_id ?? 0);
                const isSeller =
                  currentRoleId === 3 ||
                  currentUser?.role === "sales" ||
                  currentUser?.role === "saler" ||
                  currentUser?.role === "seller";

                const isBotActive = activeConversation?.status === "active";
                const isResolved = activeConversation?.status === "resolved";
                const isInputDisabled =
                  replying || isResolved || (isSeller && isBotActive);

                return (
                  <div className="chat-footer-controls">
                    <div className="answer-input-box">
                      <textarea
                        placeholder={
                          isSeller && isBotActive
                            ? "Bot is handling. Click 'Handle' to switch status to Need Human before replying..."
                            : isResolved
                              ? "Ticket is resolved."
                              : "Enter your answer..."
                        }
                        rows={3}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (!isInputDisabled) {
                              handleSendAgentReply();
                            }
                          }
                        }}
                        disabled={isInputDisabled}
                      />
                      <div className="input-actions-bar">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          accept="image/*,video/*"
                          multiple
                          style={{ display: "none" }}
                        />
                        {uploadingFile ? (
                          <div
                            className="spinner-mini"
                            style={{ margin: "0 8px" }}
                          ></div>
                        ) : (
                          <Button
                            icon={
                              <PaperClipOutlined
                                style={{ color: "#808191", fontSize: 18 }}
                              />
                            }
                            type="text"
                            className="attach-btn"
                            disabled={isInputDisabled}
                            onClick={() => fileInputRef.current?.click()}
                          />
                        )}
                        <Button
                          type="primary"
                          onClick={handleSendAgentReply}
                          loading={replying}
                          disabled={!replyText.trim() || isInputDisabled}
                          className="send-reply-btn"
                        >
                          Send
                        </Button>
                      </div>
                    </div>

                    {activeConversation?.status !== "resolved" ? (
                      <div
                        className="resolve-action-row"
                        style={{ display: "flex", gap: 12 }}
                      >
                        <Button
                          icon={<UserOutlined />}
                          onClick={
                            activeConversation?.status === "waiting_for_human"
                              ? undefined
                              : handleTakeOverTicket
                          }
                          loading={resolving}
                          style={
                            activeConversation?.status === "active"
                              ? {
                                  background:
                                    "linear-gradient(145deg, #fbe18d, #f4c93b)",
                                  color: "#111",
                                  border: "none",
                                  fontWeight: 700,
                                }
                              : {
                                  background: "rgba(52, 211, 153, 0.15)",
                                  color: "#34d399",
                                  border: "1px solid rgba(52, 211, 153, 0.3)",
                                  fontWeight: 600,
                                  cursor: "default",
                                  pointerEvents: "none",
                                }
                          }
                        >
                          {activeConversation?.status === "waiting_for_human"
                            ? "Handling (Need Human)"
                            : "Handle"}
                        </Button>
                        <Button
                          icon={<CheckCircleOutlined />}
                          className="resolve-ticket-btn"
                          onClick={handleResolveTicket}
                          loading={resolving}
                        >
                          Resolved
                        </Button>
                      </div>
                    ) : (
                      <div className="resolve-action-row">
                        <Button
                          icon={<ClockCircleOutlined />}
                          className="reopen-ticket-btn"
                          onClick={handleReopenTicket}
                          loading={resolving}
                        >
                          Reopen Ticket
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Right Context Panels */}
            <div className="context-column">
              {/* Store Basic info */}
              <div className="context-card store-main-card">
                <h3 className="store-title">{infoPackageStore?.store_name}</h3>
                <span className="store-created-date">
                  Created at {infoPackageStore?.create_at}
                </span>

                {/* PACKAGE & BILLING */}
                <div className="metadata-section">
                  <div className="section-header-title">
                    <span className="billing-icon">📋</span> PACKAGE & BILLING
                  </div>
                  <div className="meta-info-grid">
                    <div className="meta-item">
                      <span className="meta-label">Plan:</span>
                      <span className="meta-value plan-bold">
                        {infoPackageStore?.plan_name}
                      </span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Expires:</span>
                      <span className="meta-value">
                        {infoPackageStore?.date_expired}
                      </span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Payment:</span>
                      <span className="meta-value paid-pill">
                        <CheckCircleOutlined
                          style={{ color: "#52c41a", marginRight: 4 }}
                        />
                        {infoPackageStore?.status_name !== ""
                          ? infoPackageStore?.status_name
                          : "Paid"}
                      </span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Cycle:</span>
                      <span className="meta-value">
                        {infoPackageStore
                          ? infoPackageStore?.income +
                            "/" +
                            infoPackageStore?.duration +
                            " Month "
                          : ""}
                      </span>
                    </div>
                  </div>
                  <a
                    href="#billing"
                    className="billing-link-action"
                    onClick={(e) => {
                      e.preventDefault();
                      if (activeConversation?.customer_id) {
                        fetchPaymentHistory(activeConversation.customer_id);
                      }
                    }}
                  >
                    View billing history ↗
                  </a>
                </div>

                {/* MEDIA FROM CUSTOMER */}
                {mediaList.length > 0 && (
                  <div className="metadata-section media-section">
                    <div className="section-header-title">
                      📷 MEDIA FROM CUSTOMER ({mediaList.length})
                    </div>

                    {!showAllMedia ? (
                      <div className="thumbnails-row">
                        {mediaList.slice(0, 3).map((img, index) => (
                          <div
                            key={index}
                            className="thumb-item"
                            onClick={() => {
                              setLightboxImg(img);
                              setLightboxVisible(true);
                            }}
                          >
                            {isVideoUrl(img) ? (
                              <video
                                src={img}
                                muted
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <img src={img} alt="thumbnail" />
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="grouped-media-container">
                        {dateGroups.map((dateStr) => {
                          const itemsForDate = mediaItems.filter(
                            (item) =>
                              formatChicago(item.created_at, "MMM DD, YYYY") ===
                              dateStr,
                          );
                          if (itemsForDate.length === 0) return null;
                          return (
                            <div key={dateStr} className="media-date-group">
                              <h4 className="media-group-date-title">
                                {dateStr}
                              </h4>
                              <div className="media-group-items-row">
                                {itemsForDate.map((item, idx) => (
                                  <div
                                    key={idx}
                                    className="media-group-item-card"
                                    onClick={() => {
                                      setLightboxImg(item.url);
                                      setLightboxVisible(true);
                                    }}
                                  >
                                    <div className="media-thumb-wrapper">
                                      {isVideoUrl(item.url) ? (
                                        <video
                                          src={item.url}
                                          muted
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                          }}
                                        />
                                      ) : (
                                        <img src={item.url} alt="thumbnail" />
                                      )}
                                    </div>
                                    <span className="media-item-time">
                                      {formatChicago(item.created_at, "HH:mm")}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {mediaList.length > 3 && (
                      <div style={{ textAlign: "left", marginTop: "12px" }}>
                        <Button
                          type="link"
                          onClick={() => setShowAllMedia(!showAllMedia)}
                          style={{
                            padding: 0,
                            height: "auto",
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#1890ff",
                          }}
                        >
                          {showAllMedia ? "Show less" : "Show more"}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* OWNER INFORMATION */}
                <div className="metadata-section">
                  <div className="section-header-title">
                    👤 OWNER INFORMATION
                  </div>
                  <div className="meta-info-grid owner-details">
                    <div className="meta-item">
                      <span className="meta-label">Owner Name:</span>
                      <span className="meta-value">
                        {infoPackageStore?.owner_name}
                      </span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Owner Phone:</span>
                      <span className="meta-value">
                        {infoPackageStore?.phone_number}
                      </span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Owner Email:</span>
                      <span className="meta-value">
                        {infoPackageStore?.email}
                      </span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Address:</span>
                      <span className="meta-value address-val">
                        {infoPackageStore?.address}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>

      {/* Image Lightbox Modal */}
      <Modal
        visible={lightboxVisible}
        footer={null}
        onCancel={() => setLightboxVisible(false)}
        className="image-lightbox-modal"
        centered
        width={800}
      >
        {isVideoUrl(lightboxImg) ? (
          <video
            src={lightboxImg}
            controls
            autoPlay
            style={{ width: "100%", borderRadius: 8 }}
          />
        ) : (
          <img
            src={lightboxImg}
            alt="Lightbox view"
            style={{ width: "100%", borderRadius: 8 }}
          />
        )}
      </Modal>

      {/* Payment History Modal */}
      <Modal
        visible={paymentHistoryVisible}
        footer={null}
        onCancel={() => setPaymentHistoryVisible(false)}
        className="payment-history-modal"
        centered
        width={750}
      >
        <div className="payment-history-container">
          <h2 className="payment-history-title">Payment history</h2>

          <div className="payment-history-meta-grid">
            <div className="meta-col">
              <div className="meta-line">
                <span className="label">Store:</span>
                <span className="value">
                  {infoPackageStore?.store_name || "N/A"}
                </span>
              </div>
              <div className="meta-line">
                <span className="label">Owner:</span>
                <span className="value">
                  {infoPackageStore?.owner_name || "N/A"}
                </span>
              </div>
            </div>
            <div className="meta-col">
              <div className="meta-line">
                <span className="label">Type:</span>
                <span className="value">
                  {infoPackageStore?.plan_name || "N/A"}
                </span>
              </div>
              <div className="meta-line">
                <span className="label">Phone number:</span>
                <span className="value">
                  {infoPackageStore?.phone_number || "N/A"}
                </span>
              </div>
            </div>
          </div>

          <div className="payment-history-table-wrapper">
            {paymentHistoryLoading ? (
              <div className="payment-loading">
                <div className="spinner"></div>
                <span>Loading billing history...</span>
              </div>
            ) : paymentHistory.length === 0 ? (
              <div className="payment-empty">No payment history found.</div>
            ) : (
              <table className="payment-history-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Date</th>
                    <th style={{ textAlign: "center" }}>Amount</th>
                    <th style={{ textAlign: "center" }}>Payment method</th>
                    <th style={{ textAlign: "center" }}>Status</th>
                    <th style={{ textAlign: "center" }}>Reason fail</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((item, idx) => {
                    const dateParts = item.date_payment
                      ? item.date_payment.split(" ")
                      : ["", ""];
                    const dateStr = dateParts[0];
                    const timeStr = dateParts[1];
                    const isSuccess = String(item.status_pay) === "1";

                    return (
                      <tr key={idx}>
                        <td style={{ textAlign: "left" }}>
                          <div className="date-cell">
                            <span className="date-line">{dateStr}</span>
                            <span className="time-line">{timeStr}</span>
                          </div>
                        </td>
                        <td
                          className="amount-cell"
                          style={{ textAlign: "center" }}
                        >
                          {item.amount}
                        </td>
                        <td
                          className="method-cell"
                          style={{ textAlign: "center" }}
                        >
                          {item.pay_method || ""}
                        </td>
                        <td
                          className="status-cell"
                          style={{ textAlign: "center" }}
                        >
                          <span
                            className={
                              isSuccess ? "status-success" : "status-failed"
                            }
                          >
                            {isSuccess ? "Success" : "Failed"}
                          </span>
                        </td>
                        <td
                          className="reason-cell"
                          style={{ textAlign: "center" }}
                        >
                          {!isSuccess && item.reason_fail ? (
                            <span className="reason-text">
                              {item.reason_fail}
                            </span>
                          ) : (
                            ""
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TicketDetail;

